import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  ICreateBankingDetailsRequest,
  ISendInviteRequest,
  IShareDocumentsPayload,
  TCreateBankingDetailsResponse,
  IGenerateDocumentsRequest,
  IGenerateEmployeeDocumentsResponse,
  TGetEmployeesSummaryRequest,
  TGetEmployeesSummaryResponse,
  TSendInviteResponse,
  TShareDocumentsResponse,
  IDeleteEmploymentDocumentPayload,
  IUpdateTimeAttendanceRulesResponse,
  IUpdateTimeAttendanceRulesFormDataRequest,
  IUpdateEmployeeStatusResponse,
  IUpdateEmployeeStatusRequest,
  IUpdateAccountSettingsRequest,
  IUpdateAccountSettingsResponse,
} from "@/types/(company)/employees.interface";
import type {
  ICreateEmployeeCompensationRequest,
  IUpdateEmployeeCompensationRequest,
  TGetCompensationComponentsRequest,
  TGetCompensationComponentsResponse,
} from "@/types/(company)/compensation.interface";
import type {
  ICreateEmployeePersonalDetailsRequest,
  ICreateEmploymentInfoRequest,
  ICreateEmploymentInfoResponse,
  TCreateEmployeePersonalDetailsResponse,
  TGetAllSectionDataRequest,
  TGetAllSectionDataResponse,
  TGetCompanyInfoFormDataRequest,
  TGetCompanyInfoResponse,
  TGetEmployeeDataRequest,
  TGetEmployeeDataResponse,
  TGetEmployeeListingResponse,
  TGetReportingOfficeListingRequest,
  TGetReportingOfficeListingResponse,
  THeadCountResponse,
  TOrganizationalStructureResponse,
  TRecentEmployeesResponse,
  TOnboardingProgressResponse,
  TUpdateCompanyInfoFormDataRequest,
  TUpdateCompanyInfoResponse,
} from "@/types/(company)/userDashboard.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import {
  API_COMPANY_GET_INDUSTRY_TYPES,
  API_COMPANY_INFORMATION,
  API_USER_STATUS_UPDATE,
  APIEndpoints,
  MS_TIME_ATTENDANCE_RULES,
} from "@/constants/apiEndpoints";

export const companyUserApi = createApi({
  reducerPath: "companyUserApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getHeadCount",
    "getRecentEmployees",
    "getUsersById",
    "getAllUsers",
    "getCompanyInfo",
    "getPositionById",
    "getAllPositions",
    "getAllSections",
    "getGeneratedDocuments",
    "getEmployeesSummary",
  ],
  endpoints: (builder) => ({
    getHeadCount: builder.query<THeadCountResponse, void>({
      query: () => ({
        url: "/company/users/dashboard/headcount",
        method: "GET",
      }),
      providesTags: ["getHeadCount"],
    }),
    getAllUsers: builder.query<
      TGetEmployeeListingResponse,
      // `departmentIds` is accepted by EmployeeFilterDto server-side but is not
      // on IApiGeneralParams, which only carries the params shared app-wide.
      // Strings allowed because the employees toolbar reads them straight out
      // of the URL (`IEmployeesFilters.departmentIds` is `string[]`); either
      // form serialises to the same query string.
      IApiGeneralParams & {
        branchId?: number;
        departmentIds?: (number | string)[];
      }
    >({
      query: (params) => ({
        url: "/company/users/paginated",
        method: "GET",
        params,
      }),
      providesTags: ["getAllUsers"],
    }),
    // Same above query, but Infinite
    getAllEmployees: builder.infiniteQuery<
      TGetEmployeeListingResponse,
      IApiGeneralParams & { positionIds?: number; branchId?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { currentPage, totalPages } = resp.meta;
          return currentPage < totalPages ? currentPage + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/company/users/paginated",
        method: "GET",
        params: {
          page: pageParam,
          limit: queryArg.limit,
          search: queryArg.search,
          positionIds: queryArg.positionIds,
          ...(queryArg.branchId ? { branchId: queryArg.branchId } : {}),
          status: "active",
        },
      }),
    }),
    getEmployees: builder.infiniteQuery<
      TGetEmployeeListingResponse,
      {
        search: string;
        initialValue: string;
        branchId?: number;
        projectId?: number;
        status?: string;
      },
      number
    >({
      infiniteQueryOptions: {
        // Defines the starting page
        initialPageParam: 1,
        // Determines how to get the next page from the last response
        getNextPageParam: (result) => {
          if (result.meta.hasNextPage) return result.meta.currentPage + 1;
          return undefined;
        },
      },
      // query function uses the current pageParam automatically
      query: ({
        queryArg: { search, initialValue, branchId, projectId, status },
        pageParam,
      }) => ({
        url: "/company/users/paginated",
        params: {
          search,
          page: pageParam,
          limit: 10,
          userId: initialValue,
          status: status ?? "active",
          branchId,
          projectId,
        },
      }),
    }),
    getRecentEmployees: builder.query<
      TRecentEmployeesResponse,
      { limit?: number }
    >({
      query: ({ limit }) => ({
        url: "/company/users/dashboard/recent-employees",
        method: "GET",
        params: { limit },
      }),
      providesTags: ["getRecentEmployees"],
    }),
    getUsersById: builder.query<
      TGetEmployeeDataResponse,
      TGetEmployeeDataRequest
    >({
      query: (params) => {
        return {
          url: `/company/users/${params.id}`,
          method: "GET",
        };
      },
      providesTags: ["getUsersById"],
    }),
    getEmployeeOnboardingProgress: builder.query<
      TOnboardingProgressResponse,
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `/company/users/${id}/onboarding-progress`,
        method: "GET",
      }),
    }),
    getEmployeesSummary: builder.query<
      TGetEmployeesSummaryResponse,
      TGetEmployeesSummaryRequest
    >({
      query: (arg) => ({
        url: "/company/users/employees/summary",
        method: "GET",
        params: arg ?? undefined,
      }),
      providesTags: ["getEmployeesSummary"],
    }),
    updateEmployeePersonalDetails: builder.mutation<
      TCreateEmployeePersonalDetailsResponse,
      ICreateEmployeePersonalDetailsRequest & {
        employeeId?: string;
      }
    >({
      query: (body) => {
        const { employeeId, ...extractedBody } = body;

        return {
          url: `/company/users/${employeeId}/personal-details`,
          method: "PUT",
          body: extractedBody,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    createEmployeePersonalDetails: builder.mutation<
      TCreateEmployeePersonalDetailsResponse,
      ICreateEmployeePersonalDetailsRequest
    >({
      query: (body) => {
        return {
          url: "/company/users/onboarding/personal-details",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    uploadEmployeeProfilePic: builder.mutation<
      any,
      { file: File; employeeId?: number }
    >({
      query: ({ file, employeeId }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: "/company/users/upload-profile-image", // adjust endpoint
          method: "POST",
          body: formData,
          params: {
            employeeId,
          },
        };
      },
      invalidatesTags: ["getAllUsers"],
    }),
    createEmploymentInfo: builder.mutation<
      ICreateEmploymentInfoResponse,
      ICreateEmploymentInfoRequest
    >({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `/company/users/onboarding/${id}/employment-info`,
          method: "POST",
          body: rest,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    updateEmployeeInfo: builder.mutation<
      ICreateEmploymentInfoResponse,
      ICreateEmploymentInfoRequest
    >({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `/company/users/${id}/employment-info`,
          method: "PUT",
          body: rest,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    getAllReportingOffices: builder.query<
      TGetReportingOfficeListingResponse,
      TGetReportingOfficeListingRequest
    >({
      query: () => ({
        url: "company/branches",
        method: "GET",
      }),
    }),
    generateDocuments: builder.mutation<
      IApiResponse<IGenerateEmployeeDocumentsResponse>,
      IGenerateDocumentsRequest
    >({
      query: (body) => {
        return {
          url: "/company/users/generate-documents",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getGeneratedDocuments"],
    }),
    getLastGeneratedDocs: builder.query<
      IApiResponse<IGenerateEmployeeDocumentsResponse>,
      { employeeId: number; employeeDocumentType?: string }
    >({
      query: ({ employeeId, employeeDocumentType }) => ({
        url: `/company/documents/${employeeId}/generated-documents`,
        method: "GET",
        params: employeeDocumentType ? { employeeDocumentType } : undefined,
      }),
      providesTags: ["getGeneratedDocuments"],
    }),
    shareDocuments: builder.mutation<
      TShareDocumentsResponse,
      IShareDocumentsPayload
    >({
      query: (body) => {
        return {
          url: "/company/users/send-documents-email",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getGeneratedDocuments"],
    }),
    resendSignLink: builder.mutation<
      { expiresInSeconds: number },
      { userId: number }
    >({
      query: (body) => ({
        url: "/sign-link/resend",
        method: "POST",
        body,
      }),
    }),
    deleteDocumentById: builder.mutation<
      TShareDocumentsResponse,
      { documentId: number }
    >({
      query: ({ documentId }) => {
        return {
          url: `/company/documents/${documentId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["getGeneratedDocuments"],
    }),
    deleteMultipleDocuments: builder.mutation<
      TShareDocumentsResponse,
      IDeleteEmploymentDocumentPayload
    >({
      query: (body) => {
        return {
          url: "/company/documents/delete-bulk",
          method: "DELETE",
          body,
        };
      },
      invalidatesTags: ["getGeneratedDocuments"],
    }),
    sendInvite: builder.mutation<TSendInviteResponse, ISendInviteRequest>({
      query: (body) => {
        return {
          url: `/company/users/${body.id}/send-platform-invitation`,
          method: "POST",
          body: body.data,
        };
      },
      invalidatesTags: ["getUsersById"],
    }),
    updateAccountSettings: builder.mutation<
      IUpdateAccountSettingsResponse,
      IUpdateAccountSettingsRequest
    >({
      query: ({ employeeId, ...body }) => ({
        url: `/company/users/${employeeId}/account-settings`,
        method: "PATCH",
        body,
      }),
    }),
    createBankingDetails: builder.mutation<
      TCreateBankingDetailsResponse,
      ICreateBankingDetailsRequest
    >({
      query: (body) => {
        return {
          url: `/company/users/onboarding/${body.id}/banking-details`,
          method: "POST",
          body: body.data,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    updateBankingDetails: builder.mutation<
      TCreateBankingDetailsResponse,
      ICreateBankingDetailsRequest
    >({
      query: (body) => {
        return {
          url: `/company/users/bank-info/${body.id}`,
          method: "PUT",
          body: body.data,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    getCompensationsComponent: builder.query<
      TGetCompensationComponentsResponse,
      TGetCompensationComponentsRequest
    >({
      query: (body) => ({
        url: "/company/compensations/components",
        method: "GET",
        body,
      }),
    }),
    createCompensation: builder.mutation<
      IApiResponse<any>,
      ICreateEmployeeCompensationRequest
    >({
      query: (body) => ({
        url: "/company/compensations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    updateCompensation: builder.mutation<
      IApiResponse<any>,
      IUpdateEmployeeCompensationRequest
    >({
      query: (body) => ({
        url: `/company/compensations/${body.id}`,
        method: "PUT",
        body: body.data,
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    assignEmployeeLeaveGroup: builder.mutation<
      IApiResponse<unknown>,
      { id: number; leaveGroupId: number | null }
    >({
      query: ({ id, leaveGroupId }) => ({
        url: `/company/users/onboarding/${id}/leave`,
        method: "POST",
        body: { leaveGroupId },
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    getCompanyInfo: builder.query<
      TGetCompanyInfoResponse,
      TGetCompanyInfoFormDataRequest
    >({
      query: () => ({
        url: API_COMPANY_INFORMATION,
        method: "GET",
      }),
      providesTags: ["getCompanyInfo"],
    }),
    updateCompanyInfo: builder.mutation<
      TUpdateCompanyInfoResponse,
      TUpdateCompanyInfoFormDataRequest
    >({
      query: (body) => ({
        url: API_COMPANY_INFORMATION,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getCompanyInfo"],
    }),
    getOrganizationalStructure: builder.query<
      TOrganizationalStructureResponse,
      void
    >({
      query: () => {
        return {
          url: "/company/organizational-structure",
          method: "GET",
        };
      },
    }),
    getAllSections: builder.query<
      TGetAllSectionDataResponse,
      TGetAllSectionDataRequest
    >({
      query: () => {
        return {
          url: "/platform-sections",
          method: "GET",
        };
      },
      providesTags: ["getAllSections"],
    }),
    getAllIndustryTypes: builder.query<
      IApiResponse<{ label: string; value: string }[]>,
      void
    >({
      query: () => ({
        url: API_COMPANY_GET_INDUSTRY_TYPES,
        method: "GET",
      }),
    }),
    updateTimeAttendanceRules: builder.mutation<
      IUpdateTimeAttendanceRulesResponse,
      { body: IUpdateTimeAttendanceRulesFormDataRequest; userId: number }
    >({
      query: ({ body, userId }) => ({
        url: `${MS_TIME_ATTENDANCE_RULES}?userId=${userId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    updateEmployeeStatus: builder.mutation<
      IUpdateEmployeeStatusResponse,
      IUpdateEmployeeStatusRequest & {
        employeeId: number;
      }
    >({
      query: (body) => {
        const { employeeId, ...extractedBody } = body;

        return {
          url: `${API_USER_STATUS_UPDATE}/${employeeId}/status`,
          method: "PUT",
          body: extractedBody,
        };
      },
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    deleteTimeAttendanceRules: builder.mutation<
      IApiPaginatedResponse<void>,
      number
    >({
      query: (attendanceRuleId) => ({
        url: APIEndpoints.Company.TimeAttendance.TimeAttendanceRule.DeleteTimeAttendanceRule(
          attendanceRuleId,
        ),
        method: "DELETE",
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    updateEmployeeFlowStep: builder.mutation<
      { success: boolean; data: { id: number; flowStep: string } },
      { employeeId: number; flowStep: string }
    >({
      query: ({ employeeId, flowStep }) => ({
        url: `/company/users/${employeeId}/flow-step`,
        method: "PUT",
        body: { flowStep },
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    completeEmployeeOnboarding: builder.mutation<
      { success: boolean; data: { id: number; flowStep: string; status: string } },
      { employeeId: number }
    >({
      query: ({ employeeId }) => ({
        url: `/company/users/${employeeId}/complete-onboarding`,
        method: "POST",
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
    moveToOnboarding: builder.mutation<
      { success: boolean; data: { id: number; status: string; flowStep: string } },
      { employeeId: number }
    >({
      query: ({ employeeId }) => ({
        url: `/company/users/${employeeId}/move-to-onboarding`,
        method: "POST",
      }),
      invalidatesTags: ["getAllUsers", "getUsersById"],
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useLazyGetAllUsersQuery,
  useLazyGetHeadCountQuery,
  useGetAllEmployeesInfiniteQuery,
  useGetEmployeesInfiniteQuery,
  useGetUsersByIdQuery,
  useGetHeadCountQuery,
  useGetCompanyInfoQuery,
  useGetAllSectionsQuery,
  useGetRecentEmployeesQuery,
  useGetAllIndustryTypesQuery,
  useGetEmployeesSummaryQuery,
  useGetLastGeneratedDocsQuery,
  useDeleteDocumentByIdMutation,
  useDeleteMultipleDocumentsMutation,
  useGetAllReportingOfficesQuery,
  useGetOrganizationalStructureQuery,
  useCreateEmploymentInfoMutation,
  useUpdateEmployeeInfoMutation,
  useGenerateDocumentsMutation,
  useShareDocumentsMutation,
  useResendSignLinkMutation,
  useSendInviteMutation,
  useGetCompensationsComponentQuery,
  useUpdateCompensationMutation,
  useAssignEmployeeLeaveGroupMutation,
  useUpdateCompanyInfoMutation,
  useCreateCompensationMutation,
  useCreateBankingDetailsMutation,
  useUpdateBankingDetailsMutation,
  useUploadEmployeeProfilePicMutation,
  useUpdateEmployeePersonalDetailsMutation,
  useCreateEmployeePersonalDetailsMutation,
  useUpdateTimeAttendanceRulesMutation,
  useUpdateEmployeeStatusMutation,
  useUpdateAccountSettingsMutation,
  useDeleteTimeAttendanceRulesMutation,
  useUpdateEmployeeFlowStepMutation,
  useCompleteEmployeeOnboardingMutation,
  useGetEmployeeOnboardingProgressQuery,
  useLazyGetEmployeeOnboardingProgressQuery,
  useMoveToOnboardingMutation,
} = companyUserApi;
